import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { fetchLeaderboard } from '../utils/leaderboardApi';
import {
  METRICS_BY_GAME,
  PERIODS,
  MIN_GAMES,
} from '../constants/leaderboard';
import LeaderboardTabs from './leaderboard/LeaderboardTabs';
import LeaderboardTable from './leaderboard/LeaderboardTable';
import './leaderboard/Leaderboard.css';

const Leaderboard = () => {
  const { user, loading: profileLoading } = useProfile();
  const navigate = useNavigate();

  // Filter state — default to Points tab
  const [activeGame, setActiveGame] = useState('points');
  const [activeMetric, setActiveMetric] = useState('points_season');
  const [activePeriod, setActivePeriod] = useState('all_time');

  // Data state
  const [rankings, setRankings] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // When the game tab changes, reset the metric to the first one for that game.
  const handleGameChange = useCallback((gameKey) => {
    setActiveGame(gameKey);
    const firstMetric = METRICS_BY_GAME[gameKey]?.[0]?.key || 'avg_time';
    setActiveMetric(firstMetric);
  }, []);

  // For Points tab: no separate period — the metric IS the period selector.
  const isPointsTab = activeGame === 'points';

  // Fetch leaderboard data whenever filters change.
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        // For points, send game_type='points' and metric=activeMetric (e.g. 'points_season')
        // Period is irrelevant for points queries but send 'all_time' as a default.
        const data = await fetchLeaderboard({
          gameType: activeGame,
          metric: activeMetric,
          period: isPointsTab ? 'all_time' : activePeriod,
        });
        if (cancelled) return;
        setRankings(data.rankings || []);
        setMyRank(data.myRank || null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load leaderboard.');
        setRankings([]);
        setMyRank(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user, activeGame, activeMetric, activePeriod]);

  // Current metric config
  const metrics = METRICS_BY_GAME[activeGame] || [];
  const currentMetric = metrics.find((m) => m.key === activeMetric) || metrics[0];
  const minGames = MIN_GAMES[activeGame] || 10;

  // Not signed in
  if (!profileLoading && !user) {
    return (
      <div className="leaderboard-page">
        <div className="leaderboard-signin">
          <p>Sign in to view leaderboards and track your rankings.</p>
          <button
            className="leaderboard-signin-btn"
            onClick={() => navigate('/auth')}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      {/* Game tabs */}
      <LeaderboardTabs activeGame={activeGame} onChange={handleGameChange} />

      {/* Filters */}
      <div className="leaderboard-filters">
        <select
          className="leaderboard-select"
          value={activeMetric}
          onChange={(e) => setActiveMetric(e.target.value)}
        >
          {metrics.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>

        {/* Period dropdown — hidden for Points tab (metric acts as period) */}
        {!isPointsTab && (
          <select
            className="leaderboard-select"
            value={activePeriod}
            onChange={(e) => setActivePeriod(e.target.value)}
          >
            {PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Min-games note — hide for Points tab */}
      {!isPointsTab && minGames > 0 && (
        <p className="leaderboard-min-note">
          Minimum {minGames} games to qualify
        </p>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="leaderboard-loading">
          <div className="leaderboard-spinner" />
          Loading leaderboard...
        </div>
      ) : error ? (
        <div className="leaderboard-error">{error}</div>
      ) : (
        <LeaderboardTable
          rankings={rankings}
          myRank={myRank}
          currentUserId={user?.id}
          scoreLabel={currentMetric?.scoreLabel || 'Score'}
          format={currentMetric?.format || 'decimal'}
        />
      )}
    </div>
  );
};

export default Leaderboard;
